/** Further calls on the pre-funding call (Paul SF-32669, 12 Oct 2026): upload a recording file or pick one of the
 *  customer's recent Aircall calls; each is listed with who / when, downloadable and pushed to LoanDisk. */
import { useEffect, useRef, useState } from "react";
import { Download, FileAudio, Loader2, Phone, Plus, Search, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  attachPrefundingExtraFromAircall, deletePrefundingExtraRecording, listPrefundingAircallCalls,
  prefundingExtraRecordingUrl, uploadPrefundingExtraRecording,
} from "@/lib/api";

const fmtDur = (s) => { const n = Number(s) || 0; return n >= 60 ? `${Math.floor(n / 60)}m ${n % 60}s` : `${n}s`; };
const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

export function ExtraCallsPanel({ appId, application, user, onChanged }) {
  const extras = application?.prefunding_call_extra_recordings || [];
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const actor = user?.email || "user";

  const upload = async (file) => {
    if (!file) return;
    const note = window.prompt(`Note for "${file.name}" (optional — e.g. "follow-up call re: payment date")`, "") ?? null;
    if (note === null) return;
    setBusy("upload");
    try {
      await uploadPrefundingExtraRecording(appId, file, note, actor);
      toast.success(`Call recording "${file.name}" attached`);
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); } finally { setBusy(null); if (fileRef.current) fileRef.current.value = ""; }
  };

  const remove = async (rec) => {
    if (!window.confirm(`Remove "${rec.original || rec.filename}" from this pre-funding call?${rec.on_loandisk ? " The copy already on LoanDisk stays." : ""}`)) return;
    setBusy(`del-${rec.id}`);
    try { await deletePrefundingExtraRecording(appId, rec.id, actor); toast.success("Recording removed"); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not remove"); } finally { setBusy(null); }
  };

  return (
    <div className="mt-3 border-t border-emerald-200 pt-3 space-y-2" data-testid="prefunding-extra-calls">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-sm font-medium text-emerald-800 flex items-center gap-1.5"><Phone className="w-4 h-4" />Further calls {extras.length > 0 && <span className="text-xs font-normal text-emerald-700">({extras.length})</span>}</p>
        <div className="flex gap-2">
          <input ref={fileRef} type="file" accept=".mp3,.wav,.m4a,.mp4,.ogg,.webm" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="extra-call-file-input" />
          <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={!!busy} data-testid="extra-call-upload-btn">
            {busy === "upload" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}Upload recording
          </Button>
          <Button variant="outline" size="sm" onClick={() => setPickerOpen(true)} disabled={!!busy} data-testid="extra-call-aircall-btn">
            <Phone className="w-4 h-4 mr-1" />Pick from Aircall
          </Button>
        </div>
      </div>
      {extras.length === 0 ? (
        <p className="text-xs text-emerald-700/80" data-testid="extra-calls-empty">No further calls attached. Upload a recording or pick one of the customer's recent Aircall calls — each goes to LoanDisk as a Call Recording.</p>
      ) : (
        <ul className="space-y-1.5" data-testid="extra-calls-list">
          {extras.map((rec) => (
            <li key={rec.id} className="flex items-start justify-between gap-2 bg-white/70 border border-emerald-100 rounded px-2.5 py-1.5 text-xs" data-testid={`extra-call-${rec.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <FileAudio className="w-3.5 h-3.5 text-emerald-700 shrink-0" />
                  <span className="font-medium text-slate-800 truncate">{rec.source === "aircall" ? `Aircall call${rec.call_number ? ` · ${rec.call_number}` : ""}` : rec.original || rec.filename}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${rec.source === "aircall" ? "bg-sky-100 text-sky-700" : "bg-slate-100 text-slate-600"}`}>{rec.source === "aircall" ? "Aircall" : "upload"}</span>
                  {rec.on_loandisk ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">on LoanDisk</span>
                    : <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700" title="Goes to LoanDisk when the loan exists">portal only</span>}
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  {rec.source === "aircall" && rec.call_started_at ? `${fmtWhen(rec.call_started_at)} · ${fmtDur(rec.call_duration)}${rec.call_agent ? ` · ${rec.call_agent}` : ""} · ` : ""}
                  {rec.size ? `${Math.round(rec.size / 1024)} KB · ` : ""}added by {rec.added_by} {fmtWhen(rec.added_at)}
                </div>
                {rec.note && <div className="text-[11px] text-slate-700 mt-0.5 italic">“{rec.note}”</div>}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <a href={prefundingExtraRecordingUrl(appId, rec.id)} target="_blank" rel="noreferrer" className="p-1 rounded hover:bg-emerald-100 text-emerald-700" title="Download" data-testid={`extra-call-download-${rec.id}`}><Download className="w-3.5 h-3.5" /></a>
                <button type="button" onClick={() => remove(rec)} disabled={busy === `del-${rec.id}`} className="p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-600" title="Remove" data-testid={`extra-call-remove-${rec.id}`}>
                  {busy === `del-${rec.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
      {pickerOpen && <AircallPicker appId={appId} actor={actor} onClose={() => setPickerOpen(false)} onAttached={() => { onChanged?.(); }} />}
    </div>
  );
}

function AircallPicker({ appId, actor, onClose, onAttached }) {
  const [days, setDays] = useState(14);
  const [phone, setPhone] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [attaching, setAttaching] = useState(null);
  const [note, setNote] = useState("");

  const load = async () => {
    setLoading(true);
    try { setData(await listPrefundingAircallCalls(appId, { days, phone: phone.trim() })); }
    catch (e) { toast.error(e.response?.data?.detail || "Aircall search failed"); setData({ calls: [], errors: [String(e.message)] }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [days]); // eslint-disable-line react-hooks/exhaustive-deps

  const attach = async (call) => {
    setAttaching(call.id);
    try {
      await attachPrefundingExtraFromAircall(appId, call.id, note, actor);
      toast.success(`Aircall call from ${fmtWhen(call.started_at)} attached`);
      onAttached?.();
      setData((d) => ({ ...d, calls: (d?.calls || []).map((c) => (c.id === call.id ? { ...c, already_attached: true } : c)) }));
    } catch (e) { toast.error(e.response?.data?.detail || "Could not attach that call"); } finally { setAttaching(null); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="aircall-picker-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Phone className="w-5 h-5 text-sky-600" />Attach an Aircall call</DialogTitle>
          <DialogDescription>Recent calls to the customer's numbers{data?.numbers?.length ? ` (${data.numbers.join(", ")})` : ""}. Only calls with a recording can be attached.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-end gap-2 text-xs">
          <div className="flex-1 min-w-[180px]">
            <label className="text-[11px] font-medium text-slate-600">Search another number</label>
            <div className="flex gap-1">
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="07… or +44…" className="h-8 text-xs" data-testid="aircall-picker-phone" onKeyDown={(e) => e.key === "Enter" && load()} />
              <Button size="sm" variant="outline" className="h-8" onClick={load} disabled={loading} data-testid="aircall-picker-search"><Search className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
          <div>
            <label className="text-[11px] font-medium text-slate-600">Window</label>
            <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="h-8 border rounded px-1.5 text-xs bg-white block" data-testid="aircall-picker-days">
              <option value={7}>Last 7 days</option><option value={14}>Last 14 days</option><option value={30}>Last 30 days</option><option value={90}>Last 90 days</option>
            </select>
          </div>
          <div className="flex-1 min-w-[180px]">
            <label className="text-[11px] font-medium text-slate-600">Note (optional, saved with the call)</label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. follow-up on start date" className="h-8 text-xs" data-testid="aircall-picker-note" />
          </div>
        </div>
        <div className="max-h-[50vh] overflow-y-auto border rounded" data-testid="aircall-picker-list">
          {loading && <div className="p-4 text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Searching Aircall…</div>}
          {!loading && data && data.calls?.length === 0 && (
            <div className="p-4 text-sm text-muted-foreground" data-testid="aircall-picker-empty">{data.message || `No calls found in the last ${days} days${data.errors?.length ? ` (Aircall: ${data.errors[0]})` : ""}.`}</div>
          )}
          {!loading && (data?.calls || []).map((c) => (
            <div key={c.id} className="flex items-center justify-between gap-2 px-3 py-2 border-b last:border-b-0 text-xs" data-testid={`aircall-call-${c.id}`}>
              <div className="min-w-0">
                <div className="font-medium text-slate-800">{fmtWhen(c.started_at)} · {fmtDur(c.duration)} · {c.direction === "inbound" ? "Inbound" : "Outbound"}</div>
                <div className="text-[11px] text-slate-500">{c.number}{c.agent ? ` · ${c.agent}` : ""}{!c.has_recording ? " · no recording" : ""}</div>
              </div>
              {c.already_attached ? (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 shrink-0">attached</span>
              ) : (
                <Button size="sm" variant="outline" className="h-7 text-xs shrink-0" disabled={!c.has_recording || attaching === c.id} onClick={() => attach(c)} data-testid={`aircall-attach-${c.id}`}>
                  {attaching === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Plus className="w-3.5 h-3.5 mr-1" />Attach</>}
                </Button>
              )}
            </div>
          ))}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose} data-testid="aircall-picker-close">Done</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
